import { useEffect, useState } from 'react';
import { challengeAPI } from '../lib/api';

/**
 * Consolidates Home-page data fetching. Today that's exactly one network
 * call (`/api/challenges/my`). The hook also derives the "most actionable"
 * challenge so HomePage stays free of business logic.
 *
 * Priority for what to surface in the Active Challenge card:
 *   1. pending-incoming  → the user needs to accept/decline
 *   2. accepted          → match in progress, needs a result
 *   3. pending-outgoing  → user is waiting on someone else
 *   4. nothing           → render the empty state
 *
 * Admins have no player row; we still fetch (the endpoint returns an
 * empty list rather than 4xx) so behavior is predictable, but the empty
 * state on Home reads correctly for admins.
 */
export default function useHomeData(currentPlayerId) {
  const [loading, setLoading] = useState(true);
  const [actionableChallenge, setActionableChallenge] = useState(null);
  const [counts, setCounts] = useState({ incoming: 0, outgoing: 0, accepted: 0 });

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      try {
        const res = await challengeAPI.getMy();
        if (cancelled) return;
        const all = res.data?.challenges || [];
        // Only consider non-terminal challenges
        const active = all.filter((c) =>
          c.status === 'pending' || c.status === 'accepted'
        );
        const incoming = active.filter((c) =>
          c.status === 'pending' && c.challenged_id === currentPlayerId
        );
        const accepted = active.filter((c) => c.status === 'accepted');
        const outgoing = active.filter((c) =>
          c.status === 'pending' && c.challenger_id === currentPlayerId
        );
        // Priority: incoming → accepted → outgoing
        const pick = incoming[0] || accepted[0] || outgoing[0] || null;
        setActionableChallenge(pick);
        setCounts({
          incoming: incoming.length,
          accepted: accepted.length,
          outgoing: outgoing.length,
        });
      } catch (err) {
        // Network errors are non-fatal on Home — keep the empty state.
        if (!cancelled) {
          setActionableChallenge(null);
          setCounts({ incoming: 0, outgoing: 0, accepted: 0 });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (currentPlayerId) {
      fetchData();
    } else {
      // No player row (e.g. admin) — skip the fetch entirely.
      setLoading(false);
    }
    return () => { cancelled = true; };
  }, [currentPlayerId]);

  return { loading, actionableChallenge, counts };
}
